import { estimate, formatPer100, MODEL, PRIME } from '../../core/estimate.js';
import { fmtMoney } from '../../intake/parseAnswer.js';
import type { Factor, ScoreResult } from '../../core/types.js';
import { PageHead, Term } from '../components.js';
import { useWorkspace } from '../state.js';

const per100 = formatPer100;

function Dots({ p }: { p: number }) {
  const v = p * 100;
  const full = Math.floor(v);
  const frac = v - full;
  return (
    <div className="dots" role="img" aria-label={`About ${per100(p)} in 100`}>
      {Array.from({ length: 100 }, (_, i) => (
        <span key={i} className={i < full ? 'on' : i === full && frac >= 0.25 ? 'part' : ''} />
      ))}
    </div>
  );
}

function BandCard({ r }: { r: ScoreResult }) {
  const cls = r.band === 'lower-risk' ? 'lower' : r.band === 'higher-risk' ? 'higher' : 'typical';
  const years = MODEL.meta.outcome.calibratedOnFiscalYears.join('–');
  const h = MODEL.meta.outcome.horizonMonths / 12;
  return (
    <section className="card glow band-card">
      <div className="eyebrow">Repayment risk among approved SBA loans</div>
      <div className={`band ${cls}`} style={{ marginTop: 8 }}>{r.bandLabel}</div>
      {r.band === 'out-of-program' && <p className="callout warn">{r.notes[0]}</p>}
      <p style={{ marginTop: 10 }}>
        About <strong className="num">{per100(r.estimatedChargeOffRate)} in 100</strong> SBA loans like this one, approved in
        FY{years}, were <Term id="charge-off">charged off</Term> within {h} years. Across all loans it was about{' '}
        <strong className="num">{per100(MODEL.global.rate)} in 100</strong>.
      </p>
      <Dots p={r.estimatedChargeOffRate} />
      <p className="small muted">
        SBA only publishes approved loans, so this describes how loans like yours did <em>after</em> approval. It is not
        your chance of being approved, and it knows nothing about your credit, cash flow or collateral.
      </p>
    </section>
  );
}

function Trend() {
  const t = MODEL.meta.backtest.recentTrend;
  const max = Math.max(...t.map((x) => x.rate));
  return (
    <section className="card">
      <h3>Charge-offs have been rising</h3>
      <div className="trend" role="img" aria-label={t.map((x) => `FY${x.fy}: ${(x.rate * 100).toFixed(2)}%`).join(', ')}>
        {t.map((x, i) => (
          <div key={x.fy} className={`col ${i === t.length - 1 ? 'latest' : ''}`}>
            <span className="lbl">{(x.rate * 100).toFixed(1)}%</span>
            <div className="bar" style={{ height: `${(x.rate / max) * 70}%` }} />
            <span className="lbl">&rsquo;{String(x.fy).slice(2)}</span>
          </div>
        ))}
      </div>
      <p className="small muted">
        Share of SBA 7(a) loans charged off within 3 years, by approval year (2020–21 left out: pandemic payment relief
        hid failures). The figure above reflects the most recent years; if the rise continues, loans made today may do
        somewhat worse.
      </p>
    </section>
  );
}

function RateCard({ r }: { r: ScoreResult }) {
  const lo = r.rate.basePct;
  const hi = r.rate.ceilingPct + 0.5;
  const pos = (v: number) => `${Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100))}%`;
  return (
    <section className="card">
      <h3>What comparable loans were charged</h3>
      <div className="spread">
        <span className="big-number">{r.rate.estimateLowPct.toFixed(2)}–{r.rate.estimateHighPct.toFixed(2)}%</span>
        <span className="muted small">middle {r.rate.estimateLikelyPct.toFixed(2)}%</span>
      </div>
      <div className="range" aria-hidden="true">
        <div className="fill" style={{ left: pos(r.rate.estimateLowPct), width: `calc(${pos(r.rate.estimateHighPct)} - ${pos(r.rate.estimateLowPct)})` }} />
        <div className="mark" style={{ left: `calc(${pos(r.rate.estimateLikelyPct)} - 1.5px)` }} />
        <div className="cap" style={{ left: pos(r.rate.ceilingPct) }} />
      </div>
      <div className="range-labels"><span>Prime {r.rate.basePct}%</span><span>SBA maximum {r.rate.ceilingPct.toFixed(2)}%</span></div>
      <dl className="kv" style={{ marginTop: 14 }}>
        <dt>Estimated monthly payment</dt><dd>{fmtMoney(r.payment.lowMonthly)}–{fmtMoney(r.payment.highMonthly)}</dd>
        <dt>Total interest at the middle rate</dt><dd>{fmtMoney(r.payment.totalInterestLikely)}</dd>
      </dl>
      <p className="small muted" style={{ marginTop: 12 }}>
        The middle 80% of rates on {r.rate.observedSpread.n.toLocaleString('en-US')} recent <Term id="variable-rate">variable-rate</Term> SBA
        loans of similar size{r.rate.spreadProvenance === 'size-term' ? ' and term' : ''}, adjusted to today&rsquo;s{' '}
        <Term id="prime">prime rate</Term> ({PRIME.pct}% as of {PRIME.asOf}). The dashed line is the most SBA lets a lender
        charge for this size ({r.rate.tierLabel}). Not a quote.
      </p>
    </section>
  );
}

function FactorRow({ f }: { f: Factor }) {
  const w = Math.min(50, Math.abs(f.weight) * 50);
  const up = f.contribution > 0;
  return (
    <div className="factor">
      <div className="name">{f.label}<small>{f.value}</small></div>
      <div className="track" aria-hidden="true">
        {f.contribution !== 0 && <div className={`bar ${up ? 'up' : 'down'}`} style={up ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />}
      </div>
      <div className={`verdict ${f.direction}`}>{f.verdict === 'Below typical' ? 'Raises risk' : f.verdict === 'Favorable' ? 'Lowers risk' : f.verdict}</div>
      <details><summary>Why</summary>{f.detail}</details>
    </div>
  );
}

export function Estimate() {
  const { ws } = useWorkspace();
  const e = estimate(ws);

  if (!e.ready) {
    return (
      <>
        <PageHead eyebrow="Estimate" title="Your estimate" />
        <div className="card empty">
          <h3>A few details first</h3>
          <p>The estimate needs {e.missing.join(', ')}.</p>
          <a className="btn primary" href="#/assistant">Continue with the assistant</a>
        </div>
      </>
    );
  }

  const r = e.result;
  return (
    <>
      <PageHead eyebrow="Estimate" title="How loans like yours have done">
        Built from every SBA 7(a) loan SBA has published, and tested on recent loans it never saw.{' '}
        <a href="#/model">How it works</a>
      </PageHead>
      <div className="split">
        <div className="stack">
          <BandCard r={r} />
          <section className="card">
            <div className="card-title"><h3>Why this estimate</h3><span className="badge">{r.confidence.label}</span></div>
            <p className="small muted">Bars to the right raised the estimate; bars to the left lowered it. They add up exactly to the result.</p>
            {r.factors.map((f) => <FactorRow key={f.key} f={f} />)}
            <ul className="small muted" style={{ marginTop: 14, paddingLeft: '1.1em' }}>
              {r.confidence.reasons.map((x) => <li key={x}>{x}</li>)}
              {r.notes.filter((n) => r.band !== 'out-of-program' || n !== r.notes[0]).map((x) => <li key={x}>{x}</li>)}
            </ul>
            <p className="small muted">
              These are patterns, not causes. For example, short loans fail more often partly because they are often used
              when cash is tight — stretching a loan does not make a business safer.
            </p>
          </section>
        </div>
        <aside className="stack">
          <RateCard r={r} />
          <Trend />
          <div className="card flat">
            <h3>What would help most</h3>
            <p className="small muted">Lenders decide mainly on cash flow, credit and collateral. Check whether your income covers the payments, and gather the documents on your checklist.</p>
            <div className="row"><a className="btn small" href="#/scenarios">Check cash flow</a><a className="btn small ghost" href="#/readiness">Checklist</a></div>
          </div>
        </aside>
      </div>
    </>
  );
}
